/**
 * Canonical color-token entry point.
 *
 * Color tokens are currently accessed three different ways across the codebase:
 *   1. `import colorsCustom from 'designTokens/colorsCustom'`  (38 files)
 *   2. `theme.palette.*` (MUI, 84 usages)
 *   3. raw hex literals scattered in `sx`/style objects
 *
 * This module is the single place new code should import color tokens from.
 * It re-exports the existing token sources (so nothing breaks) and adds a
 * `semantic` alias layer that maps human-readable names to canonical values.
 *
 * IMPORTANT: This is an ADDITIVE layer. The original exports
 * (colors / paletteMap / colorsCustom / colorsVariants) are still valid and
 * still imported elsewhere — they are NOT removed here. New code should prefer
 * `semantic` / `palette` from this file; existing imports are migration targets.
 */

import colors, { paletteMap } from './colors.js';
import colorsCustom from './colorsCustom.js';
import colorsVariants from './colorsVariants.js';

/**
 * Semantic aliases — the preferred names for new code.
 * Each maps to an existing canonical token so there is exactly one source value.
 */
const semantic = {
  // Surfaces
  background: colorsCustom.background,        // paletteMap.inkGray['100']
  paper: colorsCustom.paper,                  // paletteMap.inkGray['50']

  // Text (ink) tones
  text: {
    bold: colorsCustom.ui.dark.bold,          // paletteMap.inkGray['900']
    moderate: colorsCustom.ui.dark.moderate,  // paletteMap.inkGray['600']
    subtle: colorsCustom.ui.dark.subtle,      // paletteMap.inkGray['400']
  },

  // Borders / dividers
  border: {
    subtle: paletteMap.inkGray['200'],        // #E4E7EA
    default: paletteMap.inkGray['300'],       // #D1D6DB
  },

  // Status colors (align with MUI palette: error/warning/info/success)
  status: {
    error: paletteMap.red['500'],
    warning: paletteMap.orange['400'],
    info: paletteMap.blue['400'],
    success: paletteMap.turquoise['400'],
  },

  // Brand
  brand: {
    primary: colorsVariants.primary,          // colors.blue scale
    secondary: colorsVariants.secondary,      // colors.ultramarine scale
    highlight: colorsVariants.highlight,      // colors.yellow scale
  },
};

/**
 * Convenience accessor for a palette shade by name + tone.
 *
 * @param {string} scaleName - color scale (e.g. 'inkGray', 'blue') [Required]
 * @param {string} tone - shade key ('50'..'900') [Required]
 * @returns {string|undefined} hex value or undefined when not found
 *
 * Example usage:
 *   getColor('inkGray', '600'); // '#7A8591'
 */
const getColor = (scaleName, tone) => paletteMap?.[scaleName]?.[tone];

const colorTokens = {
  // Raw scales (array form) — same object as designTokens/colors.js default.
  colors,
  // Keyed map form ({ '50': hex, ... }) for tone lookups.
  paletteMap,
  // Existing access patterns kept as aliases (do not break current imports).
  custom: colorsCustom,
  variants: colorsVariants,
  // Preferred named tokens for new code.
  semantic,
  getColor,
};

export default colorTokens;
export { colors, paletteMap, colorsCustom, colorsVariants, semantic, getColor };
